import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Calendar,
  AlertTriangle,
  Loader2,
  Undo2,
  ClipboardList,
  GraduationCap,
  Wallet,
  X,
  CheckCircle2,
} from 'lucide-react';
import {
  fetchGroups,
  fetchAllocationsTableView,
  fetchAllocationUndoPreview,
  undoAllocation,
} from './api';

// ---------------------------------------------------------------------
// Wired to the real /allocations/table_view/ endpoint. The backend
// handles the heavy filter — the created_for_date range — returning
// every allocation (all account types) in that range, plus a `canUndo`
// flag per row (true only when the allocation's period is currently
// 'active' or 'open'). Group/officer/search filtering stays client-side
// on that (already date-filtered) result set, same pattern used
// elsewhere in this app.
//
// Undo is a two-step flow: clicking "Undo" opens a confirmation dialog
// that first calls /allocations/<id>/undo_preview/ to show exactly what
// will be deleted (including any sibling allocation on the same date)
// and how the member's balances will change, then — only on explicit
// confirm — calls /allocations/<id>/undo/ to actually do it. That call
// is admin-only; a non-admin gets back a clear permission error.
// ---------------------------------------------------------------------

const ACCOUNT_TYPE_LABELS = {
  sf: 'SF',
  regular: 'Regular',
};

// The 3 cards. "all" also drives the "All Types" dropdown option so the
// cards and the Account Type filter stay in sync (one underlying state).
const ACCOUNT_TYPE_CARDS = [
  { key: 'all', label: 'All Allocations', icon: ClipboardList },
  { key: 'sf', label: 'School Fees Allocations', icon: GraduationCap },
  { key: 'regular', label: 'Regular', icon: Wallet },
];

const matchesAccType = (accType, cardKey) => {
  if (cardKey === 'all') return true;
  return accType === cardKey;
};

const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  sf: {
    icon: (isDark) => (isDark ? 'text-indigo-400' : 'text-indigo-600'),
    active: (isDark) => (isDark ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-500 bg-indigo-50'),
  },
  regular: {
    icon: (isDark) => (isDark ? 'text-teal-400' : 'text-teal-600'),
    active: (isDark) => (isDark ? 'border-teal-500 bg-teal-900/20' : 'border-teal-500 bg-teal-50'),
  },
};

// Default date range: past 45 days, as requested.
const getPast45DaysRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 45);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

// Renders each affected balance as "current -> new". Used by both the
// undo preview (projected) and the post-undo success screen (actual).
const BalanceChangeTable = ({ balances, isDark, formatCurrency }) => {
  if (!balances) return null;

  const rows = [
    { key: 'unallocated_funds', label: 'Unallocated Funds', data: balances.unallocated_funds },
    { key: 'savings', label: balances.savings?.label || 'Savings', data: balances.savings },
    { key: 'loan_balance', label: 'Loan Balance', data: balances.loan_balance },
    { key: 'advance_balance', label: 'Advance Balance', data: balances.advance_balance },
  ].filter(row => row.data && row.data.new !== null && row.data.new !== undefined);

  if (rows.length === 0) return null;

  return (
    <div className="space-y-2 text-sm">
      {rows.map(row => (
        <div key={row.key} className="flex items-center justify-between gap-3">
          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{row.label}</span>
          <span className="flex items-center gap-2 font-semibold">
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
              {formatCurrency(row.data.current)}
            </span>
            <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>&rarr;</span>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>
              {formatCurrency(row.data.new)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

const MemberAllocations = ({ isDark = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accTypeFilter, setAccTypeFilter] = useState('all'); // shared by cards + Account Type dropdown
  const [groupFilter, setGroupFilter] = useState('all');
  const [officerFilter, setOfficerFilter] = useState('all');
  const [groups, setGroups] = useState([]);
  const [dateRange, setDateRange] = useState(getPast45DaysRange());
  const [activeDatePreset, setActiveDatePreset] = useState('past45'); // 'past45' | 'month' | 'custom' | 'all'
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  // Undo dialog state
  const [undoTarget, setUndoTarget] = useState(null); // the allocation row that was clicked
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoPreviewLoading, setUndoPreviewLoading] = useState(false);
  const [undoPreviewError, setUndoPreviewError] = useState(null);
  const [undoSubmitting, setUndoSubmitting] = useState(false);
  const [undoSubmitError, setUndoSubmitError] = useState(null);
  const [undoResult, setUndoResult] = useState(null); // final balances after a successful undo

  useEffect(() => {
    loadGroups();
    loadAllocations();
  }, [dateRange]);

  const loadGroups = async () => {
    try {
      const groupsData = await fetchGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  // Fetches allocations of ALL account types within the created_for_date
  // range — the backend handles that date filtering, and returns a
  // `canUndo` flag per row. Account type is filtered client-side (via the
  // 3 cards) so each card can show its own count/total from one dataset.
  const loadAllocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAllocationsTableView(dateRange.startDate, dateRange.endDate);
      setAllocations(response?.results || []);
    } catch (err) {
      console.error('Error loading allocations:', err);
      setError('Failed to load member allocations');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateApply = () => {
    setDateRange(tempDateRange);
    setSelectedMonth('');
    setActiveDatePreset('custom');
    setCustomDateDialogOpen(false);
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedMonth('');
    setActiveDatePreset('all');
  };

  const handlePast45Days = () => {
    setDateRange(getPast45DaysRange());
    setSelectedMonth('');
    setActiveDatePreset('past45');
  };

  // Opens the undo dialog and loads its preview (siblings + balance impact).
  const handleUndoClick = async (allocation) => {
    setUndoTarget(allocation);
    setUndoPreview(null);
    setUndoPreviewError(null);
    setUndoSubmitError(null);
    setUndoResult(null);
    setUndoPreviewLoading(true);
    try {
      const preview = await fetchAllocationUndoPreview(allocation.id);
      setUndoPreview(preview);
    } catch (err) {
      console.error('Error loading undo preview:', err);
      setUndoPreviewError(
        err.response?.data?.message || 'Failed to load the undo preview for this allocation.'
      );
    } finally {
      setUndoPreviewLoading(false);
    }
  };

  const closeUndoDialog = () => {
    if (undoSubmitting) return; // don't let it be dismissed mid-request
    setUndoTarget(null);
    setUndoPreview(null);
    setUndoPreviewError(null);
    setUndoSubmitError(null);
    setUndoResult(null);
    loadAllocations(); // in case a completed undo hasn't been reflected yet
  };

  const confirmUndo = async () => {
    if (!undoTarget) return;
    setUndoSubmitting(true);
    setUndoSubmitError(null);
    try {
      const result = await undoAllocation(undoTarget.id);
      setUndoResult(result); // swaps the dialog into the success view
      loadAllocations(); // refresh the table + card totals in the background
    } catch (err) {
      console.error('Error undoing allocation:', err);
      setUndoSubmitError(
        err.response?.data?.message || 'Failed to undo this allocation. Please try again.'
      );
    } finally {
      setUndoSubmitting(false);
    }
  };

  // Search + group + officer + meeting-date range — everything except
  // account type, so all 3 cards can be computed from one filtered set.
  const baseFiltered = useMemo(() => {
    let filtered = [...allocations];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.member?.name?.toLowerCase().includes(query) ||
        a.group?.name?.toLowerCase().includes(query) ||
        a.id?.toString().includes(query)
      );
    }

    if (groupFilter !== 'all') {
      filtered = filtered.filter(a => a.group?.id?.toString() === groupFilter);
    }

    if (officerFilter !== 'all') {
      filtered = filtered.filter(a => a.officerName === officerFilter);
    }

    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(a => {
        if (!a.meetingDate) return false;
        const date = new Date(a.meetingDate);
        const start = dateRange.startDate ? new Date(dateRange.startDate) : null;
        const end   = dateRange.endDate   ? new Date(dateRange.endDate)   : null;
        if (start && end) return date >= start && date <= end;
        if (start) return date >= start;
        if (end)   return date <= end;
        return true;
      });
    }

    return filtered;
  }, [allocations, searchQuery, groupFilter, officerFilter, dateRange]);

  const cardStats = useMemo(() => {
    return ACCOUNT_TYPE_CARDS.map(card => {
      const matching = baseFiltered.filter(a => matchesAccType(a.accType, card.key));
      const total = matching.reduce((sum, a) => sum + parseFloat(a.totalRepaid || 0), 0);
      return { ...card, count: matching.length, total };
    });
  }, [baseFiltered]);

  const filteredAllocations = useMemo(
    () => baseFiltered.filter(a => matchesAccType(a.accType, accTypeFilter)),
    [baseFiltered, accTypeFilter]
  );

  const officerOptions = useMemo(() => {
    const set = new Set();
    allocations.forEach(a => { if (a.officerName) set.add(a.officerName); });
    return Array.from(set).sort();
  }, [allocations]);

  const formatCurrency = (amount) => `KSh ${parseFloat(amount || 0).toLocaleString()}`;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const totals = useMemo(() => {
    return filteredAllocations.reduce((acc, a) => {
      acc.totalRepaid += parseFloat(a.totalRepaid || 0);
      acc.savings += parseFloat(a.savings || 0);
      acc.installmentPaid += parseFloat(a.installmentPaid || 0);
      acc.advancePaid += parseFloat(a.advancePaid || 0);
      acc.interestPaid += parseFloat(a.interestPaid || 0);
      acc.others += parseFloat(a.others || 0);
      return acc;
    }, { totalRepaid: 0, savings: 0, installmentPaid: 0, advancePaid: 0, interestPaid: 0, others: 0 });
  }, [filteredAllocations]);

  const getAccTypeBadgeColor = (accType) => {
    if (accType === 'sf') return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
    return isDark ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading member allocations...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button
          onClick={loadAllocations}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pb-32">
      {/* Account type cards - "all", "sf", "regular". Clicking one also
          moves the Account Type dropdown below, since both control the
          same underlying filter. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {cardStats.map((card) => {
          const Icon = card.icon;
          const isActive = accTypeFilter === card.key;
          const accent = CARD_ACCENT[card.key];
          return (
            <button
              key={card.key}
              onClick={() => setAccTypeFilter(card.key)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : isDark
                    ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {card.count}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatCurrency(card.total)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main panel: title, filters, table and footer count all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Member Allocations
          </h2>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {allocations.length} allocation{allocations.length === 1 ? '' : 's'} in the selected date range.
          </p>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search allocations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Group Filter */}
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Groups</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>

          {/* Account Type Filter - kept in sync with the cards above */}
          <select
            value={accTypeFilter}
            onChange={(e) => setAccTypeFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Types</option>
            <option value="sf">School Fees (SF)</option>
            <option value="regular">Regular</option>
          </select>

          {/* Officer Filter */}
          <select
            value={officerFilter}
            onChange={(e) => setOfficerFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Officers</option>
            {officerOptions.map(officer => (
              <option key={officer} value={officer}>
                {officer}
              </option>
            ))}
          </select>

          {/* Past 45 Days - default range */}
          <button
            onClick={handlePast45Days}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'past45'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Past 45 Days
          </button>

          {/* Month Selector Button - filters by meeting date */}
          <button
            onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
            className={`px-4 py-2 border rounded-lg transition-colors text-left flex items-center justify-between ${
              activeDatePreset === 'month'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <span>
              {selectedMonth !== '' ? (() => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const [year, monthIndex] = selectedMonth.split('-');
                return `${monthNames[parseInt(monthIndex)]} ${year}`;
              })() : 'Select Month (Meeting Date)'}
            </span>
            <Calendar className="w-4 h-4 ml-2" />
          </button>

          {/* Custom Date Button */}
          <button
            onClick={() => {
              setTempDateRange(dateRange);
              setCustomDateDialogOpen(true);
            }}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'custom'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  ACC TYPE
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  TOTAL REPAID
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  MEMBER NAME
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  SAVINGS
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  INST PAID
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  ADVA PAID
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  INT PAID
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  OTHERS
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredAllocations.map((a) => (
                <tr key={a.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getAccTypeBadgeColor(a.accType)}`}>
                      {ACCOUNT_TYPE_LABELS[a.accType] || a.accType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="font-semibold text-green-600">
                      {formatCurrency(a.totalRepaid)}
                    </div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {formatDate(a.meetingDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {a.member?.name || 'N/A'}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {a.group?.name || 'No Group'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-blue-600">
                    {formatCurrency(a.savings)}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-purple-600">
                    {formatCurrency(a.installmentPaid)}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-orange-600">
                    {formatCurrency(a.advancePaid)}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-amber-600">
                    {formatCurrency(a.interestPaid)}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-500">
                    {formatCurrency(a.others)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {a.canUndo ? (
                      <button
                        onClick={() => handleUndoClick(a)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          isDark
                            ? 'border-red-800 text-red-400 hover:bg-red-900/20'
                            : 'border-red-200 text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <Undo2 className="w-3.5 h-3.5" />
                        Undo
                      </button>
                    ) : (
                      <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <tr>
                <td className={`px-6 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  TOTAL
                </td>
                <td className="px-6 py-4 text-right font-bold text-green-600">
                  {formatCurrency(totals.totalRepaid)}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {filteredAllocations.length} allocations
                  </span>
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  {formatCurrency(totals.savings)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-purple-600">
                  {formatCurrency(totals.installmentPaid)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-orange-600">
                  {formatCurrency(totals.advancePaid)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-amber-600">
                  {formatCurrency(totals.interestPaid)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-gray-500">
                  {formatCurrency(totals.others)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {filteredAllocations.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No allocations found matching your filters.</p>
          </div>
        )}

        {/* Footer count */}
        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredAllocations.length} of {allocations.length} allocations
        </div>
      </div>

      {/* Month Selection Menu */}
      {monthMenuAnchor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuAnchor(null)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${monthNames[monthIndex]} ${year}`,
                  monthIndex: monthIndex,
                  year: year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    const startDate = new Date(month.year, month.monthIndex, 1);
                    const endDate = new Date(month.year, month.monthIndex + 1, 0);
                    setDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setSelectedMonth(month.value);
                    setActiveDatePreset('month');
                    setMonthMenuAnchor(null);
                  }}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleClearDateFilter}
                className={`w-full px-4 py-2 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                }`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Custom Date Range
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleClearDateFilter}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Confirmation Dialog */}
      {undoTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeUndoDialog}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b flex items-center justify-between flex-shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {undoResult ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    Allocation Undone
                  </>
                ) : (
                  <>
                    <Undo2 className="w-5 h-5 text-red-500" />
                    Undo Allocation
                  </>
                )}
              </h3>
              <button
                onClick={closeUndoDialog}
                disabled={undoSubmitting}
                className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Who/where/when — kept visible in both the preview and success
                views so it's always clear exactly which record is being
                undone, before any admin confirms. */}
            {(() => {
              const member = undoTarget.member || undoPreview?.member;
              const group = undoTarget.group || undoPreview?.group;
              const meetingDate = undoTarget.meetingDate || undoPreview?.created_for_date;
              return (
                <div className={`px-6 py-3 border-b flex-shrink-0 text-sm ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                  <div className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {member?.name || 'Unknown member'}
                    {member?.member_id && (
                      <span className={`font-normal ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({member.member_id})
                      </span>
                    )}
                  </div>
                  <div className={`mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span>Group: {group?.name || 'No Group'}</span>
                    <span>Meeting Date (Period End): {formatDate(meetingDate)}</span>
                  </div>
                </div>
              );
            })()}

            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              {/* ---------- Success view (after a completed undo) ---------- */}
              {undoResult && (
                <>
                  <div className={`rounded-lg p-4 text-sm flex gap-3 ${isDark ? 'bg-green-900/20 text-green-300 border border-green-800' : 'bg-green-50 text-green-800 border border-green-200'}`}>
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{undoResult.message}</span>
                  </div>

                  <div className={`rounded-lg border p-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      Updated balances
                    </div>
                    <BalanceChangeTable balances={undoResult.balances} isDark={isDark} formatCurrency={formatCurrency} />
                  </div>
                </>
              )}

              {/* ---------- Preview / confirm view ---------- */}
              {!undoResult && undoPreviewLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className={`ml-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Checking this allocation...
                  </span>
                </div>
              )}

              {!undoResult && !undoPreviewLoading && undoPreviewError && (
                <div className={`rounded-lg p-4 text-sm ${isDark ? 'bg-red-900/20 text-red-400 border border-red-800' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {undoPreviewError}
                </div>
              )}

              {!undoResult && !undoPreviewLoading && undoPreview && (
                <>
                  {/* Warning message from the backend (works for both the
                      allowed and blocked cases, since the backend writes
                      the appropriate message either way). */}
                  <div className={`rounded-lg p-4 text-sm flex gap-3 ${
                    undoPreview.can_undo
                      ? (isDark ? 'bg-amber-900/20 text-amber-300 border border-amber-800' : 'bg-amber-50 text-amber-800 border border-amber-200')
                      : (isDark ? 'bg-red-900/20 text-red-400 border border-red-800' : 'bg-red-50 text-red-600 border border-red-200')
                  }`}>
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{undoPreview.message}</span>
                  </div>

                  {undoPreview.can_undo && (
                    <>
                      {undoPreview.will_undo_multiple && (
                        <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wide ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                            Allocations that will be deleted ({undoPreview.allocations_to_undo.length})
                          </div>
                          <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            {undoPreview.allocations_to_undo.map((a) => (
                              <div key={a.id} className="px-4 py-2 flex items-center justify-between text-sm">
                                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                  {formatDate(a.created_for_date)}
                                </span>
                                <span className="font-semibold text-green-600">
                                  {formatCurrency(a.total_amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className={`rounded-lg border p-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className={`font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          Balances after undo
                        </div>
                        <BalanceChangeTable balances={undoPreview.balances} isDark={isDark} formatCurrency={formatCurrency} />
                      </div>
                    </>
                  )}

                  {undoSubmitError && (
                    <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-red-900/20 text-red-400 border border-red-800' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                      {undoSubmitError}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={`px-6 py-4 border-t flex justify-end gap-2 flex-shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              {undoResult ? (
                <button
                  onClick={closeUndoDialog}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={closeUndoDialog}
                    disabled={undoSubmitting}
                    className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                      isDark
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Cancel
                  </button>
                  {undoPreview?.can_undo && (
                    <button
                      onClick={confirmUndo}
                      disabled={undoSubmitting || undoPreviewLoading}
                      className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {undoSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {undoSubmitting ? 'Undoing...' : 'Yes, Undo Permanently'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberAllocations;